import { useState, useRef, useLayoutEffect, useEffect, useMemo, useCallback } from "react";
import { __ } from "services/Translator";
import api from "services/api";
import { isProfileComplete } from "utils/profileValidation";
import moneyFormatter from "services/moneyFormatter";
import { formatAmount } from "./data";
import MethodTile from "./MethodTile";
import PresetChip from "./PresetChip";
import AmountField from "./AmountField";
import BonusSelector from "./BonusSelector";
import {
  ShieldSecureIcon,
  ChevronRightIcon,
  GoBackIcon,
  KeypadIcon,
  LockIcon,
  LockSmallIcon,
} from "./icons";
import analytics from "./analytics";
import {
  useDepositEngine,
  useBonusSelection,
  buildDepositCategories,
  buildDepositMethods,
  buildBonusOptions,
  derivePresets,
  bonusReward,
  saveNextBonus,
} from "./useCashierData";

// CMS denominations are authored as whole-currency numbers in the brand base
// (EUR per the cashier engine map); they are converted to the player's wallet
// currency before display so the chip symbol and its magnitude agree.
const PRESET_BASE = "EUR";

// 200 responses whose status is a queued/processing terminal state. These are
// NOT failures: the deposit was accepted and is awaiting the PSP/back office.
const PENDING_STATUSES = ["pending", "processing", "in_review", "in_progress", "pending_approval", "queued"];

// The banner link is admin-authored and reaches players via TWO write paths: the
// cashier builder (which validates the scheme) AND the standalone CMS module
// (which does NOT). Sanitize on render as defence-in-depth so a javascript:/data:
// value from either path can never become an executable href. Allow only http(s)
// and site-relative paths; anything else drops the link (banner still shows).
function safeBannerHref(link) {
  const s = String(link || "").trim();
  if (!s) return null;
  if (/^https?:\/\//i.test(s)) return s;
  if (/^\/(?!\/)/.test(s)) return s; // leading single slash = same-site path
  return null;
}

export default function DepositView({ active, cashier = {}, claimedBonusId = null }) {
  const settings = cashier.settings || {};
  const sym = cashier.currencySymbol || "€";
  const currency = cashier.currency || "EUR";
  const fmt = useMemo(
    () => cashier.formatMoney || ((v) => sym + formatAmount(v)),
    [cashier.formatMoney, sym]
  );

  const [step, setStep] = useState(1);
  const [category, setCategory] = useState("banking");
  const [amount, setAmount] = useState("");
  const [bonusOpen, setBonusOpen] = useState(false);
  const [method, setMethod] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [invoiceUrl, setInvoiceUrl] = useState(null);
  // Per-coin crypto-address fetch state: "loading" | "error" (success lives in
  // engine.cryptoAddresses). Drives the spinner / error+retry panel.
  const [cryptoState, setCryptoState] = useState({});

  const amountTouched = useRef(false);
  const startedRef = useRef(false);
  // Synchronous re-entry lock: the `submitting` state does not flush before a
  // rapid second click during the async profile pre-check, so a ref closes that
  // double-submit window (the backend idempotency key is the final backstop).
  const submitLockRef = useRef(false);

  const numeric = useMemo(() => {
    const v = parseInt(String(amount).replace(/\D/g, ""), 10);
    return Number.isNaN(v) ? 0 : v;
  }, [amount]);

  const engine = useDepositEngine(numeric);

  // Convert a whole base-currency amount to the player's wallet currency (0/NaN
  // rate -> keep the base number so the chip is never blank).
  const toPlayerAmount = useCallback(
    (baseAmt) => {
      const conv = moneyFormatter.getFiatValue(baseAmt, PRESET_BASE, currency);
      const n = typeof conv === "number" && Number.isFinite(conv) ? Math.round(conv) : baseAmt;
      return n > 0 ? n : baseAmt;
    },
    [currency]
  );

  // Engine -> Hub-shape data. No static fake-method fallback: while the methods
  // request is in flight the view shows skeleton tiles; a loaded-but-empty list
  // (restricted region / backend failure) shows an honest empty state. Rendering
  // fabricated methods a player could select and submit against is the C1 defect.
  const categories = useMemo(
    () => buildDepositCategories(engine.sections, engine.methods, engine.cryptoCoins),
    [engine.sections, engine.methods, engine.cryptoCoins]
  );

  const methodsByCategory = useMemo(
    () => buildDepositMethods(engine.sections, engine.methods, engine.cryptoCoins, fmt),
    [engine.sections, engine.methods, engine.cryptoCoins, fmt]
  );

  // buildBonusOptions always appends "No bonus", so the real list is used as-is;
  // no fabricated +100% / +50% promos for a player with zero eligible campaigns.
  const bonusOptions = useMemo(() => buildBonusOptions(engine.campaigns, fmt), [engine.campaigns, fmt]);

  // With nothing but the synthetic "No bonus" entry there is no choice to make,
  // so the selector is not rendered at all (it used to show a gold bar whose
  // only option was "No bonus").
  const hasBonusChoice = useMemo(
    () => bonusOptions.some((o) => o.id !== "no_bonus"),
    [bonusOptions]
  );

  const presets = useMemo(() => {
    const derived = derivePresets(settings.denominations, settings.popularPreset, bonusOptions) || [];
    return derived.map((p) => ({ ...p, amt: toPlayerAmount(p.amt) }));
  }, [settings.denominations, settings.popularPreset, bonusOptions, toPlayerAmount]);

  const isCryptoCat = category === "__crypto";
  const activeMethods = methodsByCategory[category] || [];
  const hasMethods = categories.length > 0;

  // Track the selected bonus by its stable campaign id (M2): typing an amount
  // refetches/reorders the list, so a positional index would silently jump.
  const { selected: bonus, selectedIndex: bonusIndex, selectByIndex, selectById } = useBonusSelection(bonusOptions);

  // Claim handoff from the Bonuses view: preselect the claimed campaign.
  useEffect(() => {
    if (claimedBonusId != null) selectById(claimedBonusId);
  }, [claimedBonusId, selectById]);

  // Default the amount to the CMS "Most Popular" preset (in player currency)
  // until the player types.
  useEffect(() => {
    if (!amountTouched.current && settings.popularPreset) {
      setAmount(String(toPlayerAmount(settings.popularPreset)));
    }
  }, [settings.popularPreset, toPlayerAmount]);

  // Keep a valid category as engine data arrives.
  useEffect(() => {
    const ids = categories.map((c) => c.id);
    if (!ids.length) return;
    if (!ids.includes(category)) setCategory(ids[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories]);

  // Keep a valid selected method for the active category.
  useEffect(() => {
    if (!activeMethods.length) return;
    if (!activeMethods.find((m) => m.name === method.name)) {
      setMethod(activeMethods[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, methodsByCategory]);

  // Deposit-funnel: fire once when the deposit view becomes active.
  useEffect(() => {
    if (active && !startedRef.current) {
      startedRef.current = true;
      analytics.depositStarted({});
    }
  }, [active]);

  const trackRef = useRef(null);
  const step1Ref = useRef(null);
  const step2Ref = useRef(null);
  const [trackH, setTrackH] = useState(0);

  const measure = useCallback(() => {
    const el = step === 1 ? step1Ref.current : step2Ref.current;
    if (el) setTrackH(el.offsetHeight);
  }, [step]);

  useLayoutEffect(() => {
    measure();
  }, [measure, active, step, category, amount, bonusIndex, method, activeMethods.length, engine.methodsLoading, categories.length]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 200);
    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  }, [measure]);

  const bonusAmount = bonusReward(bonus, numeric);

  const chooseCategory = (id) => {
    setCategory(id);
    analytics.categorySelected({ category: id });
  };

  // Fetch a crypto deposit address with an explicit loading/error lifecycle
  // (H4). engine.fetchCryptoAddress caches successes and never stores a failure,
  // so Retry re-requests cleanly.
  const loadCryptoAddress = useCallback(
    async (coin) => {
      if (!coin || engine.cryptoAddresses[coin]) return;
      setCryptoState((s) => ({ ...s, [coin]: "loading" }));
      const data = await engine.fetchCryptoAddress(coin);
      setCryptoState((s) => ({ ...s, [coin]: data && data.address ? "done" : "error" }));
    },
    // engine.fetchCryptoAddress is a stable useCallback; cryptoAddresses gates the cache.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine.fetchCryptoAddress, engine.cryptoAddresses]
  );

  const selectMethod = (m) => {
    setMethod(m);
    analytics.methodSelected({ method: m.method_id || m.name });
    // NOTE: crypto deposit addresses are NOT fetched on selection. Each fetch
    // mints a persistent wallet/provider address, so we only generate one when
    // the player explicitly asks for it (the "Generate address" button below).
    // Switching between coins therefore never creates unused wallets.
  };

  // Per-method min/max in the player's currency (min_deposit / max_deposit
  // in_player_currency). 0 or absent means no bound.
  const methodMin = useMemo(() => {
    const raw = method && method.minPlayer != null ? String(method.minPlayer) : "";
    const n = Number(raw.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [method]);

  const methodMax = useMemo(() => {
    if (isCryptoCat) return 0;
    const raw = method && method.maxPlayer != null ? String(method.maxPlayer) : "";
    const n = Number(raw.replace(/[^0-9.]/g, ""));
    return Number.isFinite(n) && n > 0 ? n : 0;
  }, [method, isCryptoCat]);

  // Enforce the selected method's min/max against the entered amount. min = 0 is
  // treated as no minimum (no message, no block); max = 0 as no upper bound.
  const validationError = useMemo(() => {
    if (isCryptoCat || numeric <= 0) return null;
    if (methodMin > 0 && numeric < methodMin) {
      return __("Minimum deposit is") + " " + fmt(methodMin);
    }
    if (methodMax > 0 && numeric > methodMax) {
      return __("Maximum deposit is") + " " + fmt(methodMax);
    }
    return null;
  }, [isCryptoCat, numeric, methodMin, methodMax, fmt]);

  const handleAmountChange = (raw) => {
    amountTouched.current = true;
    // Sanitize to digits only. The upper bound is NOT truncated here: the
    // per-method max drives the "Maximum deposit is X" validation message and
    // the disabled CTA, so an over-max amount is surfaced, not silently clamped.
    const digits = String(raw).replace(/\D/g, "").slice(0, 7);
    setAmount(digits);
    analytics.amountSet({ amount: parseInt(digits, 10) || 0 });
  };

  // The choice is persisted the moment it changes, so it survives leaving the
  // cashier and is what the back office sees. The deposit submit still sends the
  // campaign explicitly, so a failed save is not allowed to interrupt anything
  // (saveNextBonus resolves false instead of throwing).
  const chooseBonus = (i) => {
    selectByIndex(i);
    const opt = bonusOptions[i];
    analytics.bonusSelected({ campaign: opt && opt.id });
    if (opt) saveNextBonus(opt.id);
  };

  const handleSubmit = async () => {
    // MF-1 (guard against re-entry): ignore clicks while a submit is in flight.
    if (submitting || submitLockRef.current) return;
    submitLockRef.current = true;

    // FR-18: under progressive-profiling registration the billing identity is
    // not collected at sign-up, so the Cashier is the enforced collection point.
    // Refuse to fire a deposit with an incomplete profile and open the blocking
    // profile modal instead (the backend guard is the authoritative safety net).
    try {
      const profileRes = await api.get("/profile");
      if (profileRes && profileRes.data && !isProfileComplete(profileRes.data)) {
        submitLockRef.current = false;
        // The back office writes a short note and a longer explanation per
        // payment method; pass them along so the modal explains why THIS rail
        // asks for the details instead of showing generic copy.
        window.dispatchEvent(
          new CustomEvent("profile-gate-required", {
            detail: {
              methodName: method && method.label,
              note: method && method.profileDetailsNote,
              help: method && method.profileDetailsHelp,
            },
          })
        );
        return;
      }
    } catch (_) {
      // If the pre-check fails, let the backend guard enforce completeness.
    }

    // MF-1 (a): disable the CTA the instant submit fires and keep it disabled
    // through redirect/response - the primary double-charge guard.
    setSubmitting(true);
    setResult(null);
    analytics.depositSubmitted({
      amount: numeric,
      method: method && (method.method_id || method.name),
      campaign: bonus && bonus.id,
    });

    try {
      const res = await engine.submitDeposit({
        amount: numeric,
        method: (method && (method.method_id || method.name)) || "",
        campaign: (bonus && bonus.id) || "no_bonus",
      });
      const data = (res && res.data) || {};
      const status = data.status;
      analytics.depositResult({ http: res && res.status, status });

      if (res && res.status === 200) {
        if (status === "redirect" && data.redirectUrl && /^https?:/i.test(data.redirectUrl)) {
          window.location.href = data.redirectUrl; // keep disabled: navigating away
          return;
        }
        if (status === "APPROVED") {
          // Terminal: deposit accepted. Keep the CTA disabled so it cannot re-fire.
          setResult({ ok: true, message: data.message || __("Your deposit was successful.") });
          return;
        }
        if (status === "additional_fields" || status === "webpayment") {
          const url = (data.data && data.data.invoice_url) || data.invoice_url || data.redirectUrl;
          if (url && /^https?:/i.test(url) && !data.invoice_url && !(data.data && data.data.invoice_url)) {
            window.location.href = url; // hosted redirect
            return;
          }
          if (url) {
            // Terminal: hosted iframe checkout shown. Keep the CTA disabled.
            setInvoiceUrl(url);
            return;
          }
          // additional_fields without any URL: this redesign does not carry the
          // legacy per-method form flow (Interac/Counos/MoneyMatrix-style), and
          // a Deposit row already exists server-side — surface an explicit
          // message and KEEP the CTA disabled so it cannot re-fire and stack
          // more pending rows. (No such method is enabled on GCC today; this is
          // the safety net if one ever gets switched on.)
          setResult({
            ok: false,
            message: __("This payment method is not supported here yet. Please contact support to complete your deposit."),
          });
          return;
        }
        if (status && PENDING_STATUSES.includes(String(status).toLowerCase())) {
          // Accepted but not yet settled (M5): show a neutral processing state,
          // NOT a hard failure. Keep the CTA disabled so it cannot re-fire.
          setResult({
            ok: true,
            pending: true,
            message: data.message ? __(data.message) : __("Your deposit is being processed."),
          });
          return;
        }
      }
      setResult({ ok: false, message: data.message ? __(data.message) : __("Deposit could not be completed.") });
      setSubmitting(false);
      submitLockRef.current = false;
    } catch (e) {
      setResult({ ok: false, message: __("Deposit could not be completed.") });
      setSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const methodDisplay = method.label || method.short || method.name || "";
  const cta1Label = __("Continue with") + " " + methodDisplay;
  // Mobile CTA: long method names ("Continue with Credit / Debit Card")
  // overflow the pill on 390px screens — derive a compact name for the small
  // breakpoint ("INTERAC Online" -> "INTERAC", any card variant -> "card").
  const methodDisplayShort = (() => {
    const l = String(methodDisplay || "").trim();
    if (l.length <= 12) return l;
    if (/credit|debit|card/i.test(l)) return __("card");
    const first = l.split(/[\s/]+/)[0];
    return first.length >= 4 ? first : l;
  })();
  const cta1LabelShort = __("Continue with") + " " + methodDisplayShort;
  const cta2Label = __("Deposit") + " " + fmt(numeric);
  const bonusLine =
    bonusAmount > 0 ? (
      <>
        {__("You'll play with")} <em>{fmt(numeric + bonusAmount)}</em> {__("in total")}
      </>
    ) : (
      __("Deposit only, no bonus applied")
    );
  const bonusNote =
    bonus && bonus.isPreselected ? __("Picked for you — the welcome offer from your sign-up.") : null;

  const activeCoin = isCryptoCat && method.isCrypto ? method.coin : null;
  const cryptoAddress = activeCoin ? engine.cryptoAddresses[activeCoin] : null;
  const cryptoAddrState = activeCoin ? cryptoState[activeCoin] : null;

  return (
    <div className={step === 2 ? "dep on2" : "dep"}>
      {settings.bannerImage ? (
        safeBannerHref(settings.bannerLink) ? (
          <a className="dep-banner" href={safeBannerHref(settings.bannerLink)} target="_blank" rel="noopener noreferrer">
            <img className="dep-banner__img" src={settings.bannerImage} alt="" />
          </a>
        ) : (
          <div className="dep-banner">
            <img className="dep-banner__img" src={settings.bannerImage} alt="" />
          </div>
        )
      ) : null}
      {!isCryptoCat && (
        <div className="stepper">
          <div className={step === 1 ? "s on" : "s done"}>
            <span className="dot">1</span>
            {__("Method")}
          </div>
          <div className="bar" style={{ "--p": step === 2 ? "100%" : "50%" }} />
          <div className={step === 2 ? "s on" : "s"}>
            <span className="dot">2</span>
            {__("Amount")}
          </div>
        </div>
      )}

      <div className="track" ref={trackRef} style={{ height: trackH ? `${trackH}px` : undefined }}>
        <div className="step s1" ref={step1Ref} inert={step === 2 ? "" : undefined}>
          {engine.methodsLoading ? (
            <>
              <div className="cattabs">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="cattab skel-tab" aria-hidden="true" />
                ))}
              </div>
              <div className="methods">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="tile skel-tile" aria-hidden="true" />
                ))}
              </div>
            </>
          ) : !hasMethods ? (
            <div className="note-line dep-empty">
              {__("No payment methods are available for your region right now. Please contact support.")}
            </div>
          ) : (
            <>
              <div className="cattabs">
                {categories.map((cat) => (
                  <div
                    key={cat.id}
                    className={category === cat.id ? "cattab sel" : "cattab"}
                    onClick={() => chooseCategory(cat.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        chooseCategory(cat.id);
                      }
                    }}
                  >
                    {cat.label}
                  </div>
                ))}
              </div>

              {categories.map((cat) => (
                <div key={cat.id} className={category === cat.id ? "panel on" : "panel"}>
                  <div className="methods">
                    {(methodsByCategory[cat.id] || []).map((m) => (
                      <MethodTile key={m.name} method={m} selected={method.name === m.name} onSelect={selectMethod} />
                    ))}
                  </div>

                  {cat.id === "__crypto" && method.isCrypto ? (
                    <div className="amount-wrap crypto-addr" style={{ marginTop: "14px" }}>
                      <div className="lab">
                        {method.coin} {__("Address")}
                      </div>
                      {cryptoAddress && cryptoAddress.address ? (
                        <>
                          <input
                            className="val"
                            readOnly
                            value={cryptoAddress.address}
                            onFocus={(e) => e.target.select()}
                            style={{
                              width: "100%",
                              fontSize: "14px",
                              // A wallet address must be readable/copyable, so override the
                              // giant-number gradient-clip treatment with solid ink.
                              color: "var(--ink)",
                              background: "none",
                              WebkitTextFillColor: "var(--ink)",
                              WebkitBackgroundClip: "border-box",
                              letterSpacing: "0",
                            }}
                          />
                          {cryptoAddress.tag ? (
                            <div className="limits">
                              {__("Tag/Memo")}: <b>{cryptoAddress.tag}</b>
                            </div>
                          ) : null}
                          <div className="limits">
                            {__("Only send")} {method.coin} {__("to this address")}.
                          </div>
                        </>
                      ) : cryptoAddrState === "loading" ? (
                        <div className="crypto-status">
                          <span className="gcc-spin" aria-hidden="true" />
                          <span>{__("Generating your deposit address...")}</span>
                        </div>
                      ) : cryptoAddrState === "error" ? (
                        <div className="crypto-status err">
                          <span>{__("We couldn't generate your deposit address. Please try again.")}</span>
                          <button type="button" className="retry" onClick={() => loadCryptoAddress(method.coin)}>
                            {__("Retry")}
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className="crypto-generate"
                          onClick={() => loadCryptoAddress(method.coin)}
                        >
                          {__("Generate")} {method.coin} {__("deposit address")}
                        </button>
                      )}
                    </div>
                  ) : null}
                </div>
              ))}

              <div className="secure-row">
                <ShieldSecureIcon />
                <span>
                  <b>{__("256-bit encrypted.")}</b> {__("You complete payment on a secure hosted checkout.")}
                </span>
              </div>

              {!isCryptoCat && (
                <button className="cta" style={{ marginTop: "18px" }} onClick={() => setStep(2)}>
                  <span className="cta-lbl-full" style={{ whiteSpace: "nowrap" }}>{cta1Label}</span>
                  <span className="cta-lbl-short" style={{ whiteSpace: "nowrap" }}>{cta1LabelShort}</span>
                  <ChevronRightIcon />
                </button>
              )}
            </>
          )}
        </div>

        <div className="step s2" ref={step2Ref} inert={step === 1 ? "" : undefined}>
          <div
            className="goback"
            onClick={() => setStep(1)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setStep(1);
              }
            }}
          >
            <GoBackIcon />
            {__("Go back")}
          </div>

          <div className="presets">
            {presets.map((p, i) => (
              <PresetChip
                key={`${i}-${p.amt}`}
                preset={p}
                symbol={sym}
                selected={numeric === p.amt}
                onSelect={(amt) => handleAmountChange(String(amt))}
              />
            ))}
          </div>

          <AmountField
            label={__("Deposit amount")}
            symbol={sym}
            value={amount}
            onChange={handleAmountChange}
            maxLength={7}
            inputId="gcc-deposit-amt"
            limits={
              <>
                {methodMin > 0 ? (
                  <>
                    {__("Min")} <b>{fmt(methodMin)}</b>
                  </>
                ) : null}
                {methodMin > 0 && methodMax > 0 ? <>&nbsp;·&nbsp;</> : null}
                {methodMax > 0 ? (
                  <>
                    {__("Max")} <b>{fmt(methodMax)}</b>
                  </>
                ) : null}
              </>
            }
          />

          <div
            className="keypad-hint"
            onClick={() => {
              const el = document.getElementById("gcc-deposit-amt");
              if (el) {
                el.focus();
                el.select();
              }
            }}
          >
            <span>
              {__("Prefer a custom amount?")} <b>{__("Tap to type")}</b>
            </span>
            <KeypadIcon />
          </div>

          {hasBonusChoice ? (
            <BonusSelector
              options={bonusOptions}
              selectedIndex={bonusIndex}
              onSelect={chooseBonus}
              open={bonusOpen}
              setOpen={setBonusOpen}
              title={bonus ? bonus.title : __("No bonus")}
              line={bonusLine}
              note={bonusNote}
            />
          ) : null}

          <div className="paying-note">
            {__("Paying with")} <b>{method.label || method.name}</b>
          </div>

          {validationError ? <div className="val-msg">{validationError}</div> : null}

          {result ? (
            <div className="paying-note" style={{ color: result.ok ? undefined : "#ff6b6b" }}>
              {result.message}
            </div>
          ) : null}

          <button
            className="cta"
            onClick={handleSubmit}
            disabled={submitting || numeric <= 0 || !!validationError}
          >
            <LockIcon />
            <span>
              {result && result.ok
                ? __("Done")
                : submitting
                ? __("Processing...")
                : cta2Label}
            </span>
          </button>

          <div className="under-cta">
            <LockSmallIcon />
            {__("Secure hosted checkout · 3-D Secure protected")}
          </div>

          {invoiceUrl ? (
            <div style={{ marginTop: "16px" }}>
              <iframe
                title={__("Secure payment")}
                src={invoiceUrl}
                style={{ width: "100%", height: "480px", border: "0", borderRadius: "10px" }}
                allow="accelerometer; autoplay; camera; gyroscope; payment"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
